/** @jsxImportSource @emotion/react */

import { Edit, Undo, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from "@mui/material";
import BaseButton from "components/common/Button/Base";
import IconButton from "components/common/Button/Icon";
import BaseInput from "components/common/Input/Base";
import { PASSWORD_REG_EX, RequestState } from "data/user.data";
import { SeteukBusiness } from "models/business.model";
import {
  DeviceVerification,
  UpdateUserData,
  UserInfo,
} from "models/user.model";
import {
  checkDeviceVeriCode,
  getBusinessList,
  sendAuthCodeOnDevice,
  updateUserInfo,
} from "pages/remotes";
import { useEffect, useState } from "react";
import { PALETTE } from "styles/base/variables";
import AlertDialog, { AlertData } from "../Alert";

interface UserDialogProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setIsUserRefresh: (value: boolean) => void;
  userInfo: UserInfo | null;
}

type TabType = "account" | "taxOffice" | "newzen";

type AccountInfoIdType = "name" | "taxAgentNumber" | "mobileNo" | "email";

type NewzenInfoIdType =
  | "newzenId"
  | "newzenPw"
  | "newzenServerIp"
  | "newzenServerDrive";

type UserInfoIdType = AccountInfoIdType | NewzenInfoIdType;

type VerificationIdType = "verificationCode" | "verificationId";

type AccountInfoData = {
  [key in AccountInfoIdType]: string;
};

type AccountInfoState = {
  [key in AccountInfoIdType]: RequestState;
};

type NewzenData = {
  [key in NewzenInfoIdType]: string;
};

type NewZenState = {
  [key in NewzenInfoIdType]: RequestState;
};

interface VeriRequestState extends RequestState {
  isVerified: boolean;
}

type VerificationState = {
  [key in VerificationIdType]: VeriRequestState;
};

const tabLabel: { [key in TabType]: string } = {
  account: "계정",
  taxOffice: "세무사무소",
  newzen: "세무사랑",
};

const accountLabel: { [key in AccountInfoIdType]: string } = {
  name: "이름",
  taxAgentNumber: "세무사관리번호",
  mobileNo: "모바일번호",
  email: "이메일",
};

const newzenLabel: { [key in NewzenInfoIdType]: string } = {
  newzenId: "아이디",
  newzenPw: "비밀번호",
  newzenServerIp: "서버 IP",
  newzenServerDrive: "저장 드라이브",
};

const REGEXP_IPV4_ADDR =
  /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}/;
const REGEXP_DRIVE_LETTER = /^[A-Z]$/;

const panelCss = css`
  min-height: 200px;
`;

function UserDialog({
  isOpen,
  setIsOpen,
  setIsUserRefresh,
  userInfo,
}: UserDialogProps) {
  const accountInitialInfo: AccountInfoData = {
    name: userInfo?.name || "",
    taxAgentNumber: userInfo?.taxAgentNumber || "",
    mobileNo: userInfo?.mobileNo || "",
    email: userInfo?.email || "",
  };
  const accountInitialState: AccountInfoState = {
    name: {
      errMsg: userInfo?.name ? `${tabLabel.account} ${accountLabel.name}` : "",
      isValid: false,
    },
    taxAgentNumber: {
      errMsg: userInfo?.taxAgentNumber
        ? `${tabLabel.account} ${accountLabel.taxAgentNumber}`
        : "",
      isValid: false,
    },
    mobileNo: {
      errMsg: userInfo?.mobileNo
        ? `${tabLabel.account} ${accountLabel.mobileNo}`
        : "",
      isValid: false,
    },
    email: {
      errMsg: userInfo?.email
        ? `${tabLabel.account} ${accountLabel.email}`
        : "",
      isValid: true,
      isDisabled: true,
    },
  };

  const newzenInitialInfo: NewzenData = {
    newzenId: userInfo?.newzenId || "",
    newzenPw: userInfo?.newzenPw || "",
    newzenServerIp: userInfo?.newzenServerIp || "",
    newzenServerDrive: userInfo?.newzenServerDrive || "",
  };
  const newzenInitialState: NewZenState = {
    newzenId: {
      errMsg: userInfo?.newzenId
        ? `${tabLabel.newzen} ${newzenLabel.newzenId}`
        : "",
      isValid: false,
    },
    newzenPw: {
      errMsg: userInfo?.newzenPw
        ? `${tabLabel.newzen} ${newzenLabel.newzenPw}`
        : "",
      isValid: false,
      isHidden: true,
    },
    newzenServerIp: {
      errMsg: userInfo?.newzenServerIp
        ? `${tabLabel.newzen} ${newzenLabel.newzenServerIp}`
        : "",
      isValid: false,
    },
    newzenServerDrive: {
      errMsg: userInfo?.newzenServerDrive
        ? `${tabLabel.newzen} ${newzenLabel.newzenServerDrive}`
        : "",
      isValid: false,
    },
  };

  const veriInitialInfo: DeviceVerification = {
    verificationCode: "",
    verificationId: "",
  };
  const veriInitialState: VerificationState = {
    verificationCode: {
      errMsg: "",
      isValid: false,
      isLoading: false,
      isVerified: false,
    },
    verificationId: {
      errMsg: "",
      isValid: false,
      isLoading: false,
      isVerified: false,
    },
  };

  const [selectedTab, setSelectedTab] = useState<TabType>("account");
  const [isBizLoading, setIsBizLoading] = useState(false);
  const [specialBiz, setSpecialBiz] = useState<SeteukBusiness | null>(null);
  const [accountInfo, setAccountInfo] =
    useState<AccountInfoData>(accountInitialInfo);
  const [accountState, setAccountState] =
    useState<AccountInfoState>(accountInitialState);
  const [newzenInfo, setNewzenInfo] = useState<NewzenData>(newzenInitialInfo);
  const [newzenState, setNewzenState] =
    useState<NewZenState>(newzenInitialState);
  const [userPassword, setUserPassword] = useState("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [verificationData, setVerificationData] =
    useState<DeviceVerification>(veriInitialInfo);
  const [verificationState, setVerificationState] =
    useState<VerificationState>(veriInitialState);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  useEffect(() => {
    getBizList();
  }, []);

  const closeDialog = () => {
    setIsOpen(false);
    setTimeout(() => {
      resetUserDialog();
    }, 500);
  };

  const resetUserDialog = () => {
    setSelectedTab("account");
    setAccountInfo(accountInitialInfo);
    setAccountState(accountInitialState);
    setNewzenInfo(newzenInitialInfo);
    setNewzenState(newzenInitialState);
    setUserPassword("");
    setVerificationData(veriInitialInfo);
    setVerificationState(veriInitialState);
    closeConfirmDialog();
  };

  const openConfirmDialog = () => {
    if (!userInfo?.id) {
      setAlertDialogData({
        message: "유저 정보를 불러오는데 실패했습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
      return;
    }
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setTimeout(() => {
      resetConfirmDialog();
    }, 500);
  };

  const resetConfirmDialog = () => {
    setUserPassword("");
    setVerificationData({
      verificationCode: "",
      verificationId: "",
    });
    setVerificationState({
      verificationCode: {
        errMsg: "",
        isValid: false,
        isLoading: false,
        isVerified: false,
      },
      verificationId: {
        errMsg: "",
        isValid: false,
        isLoading: false,
        isVerified: false,
      },
    });
  };

  const getBizList = () => {
    setIsBizLoading(true);
    getBusinessList()
      .then((res) => {
        setIsBizLoading(false);
        const bizList = res.data;
        bizList.forEach((biz: any) => {
          if (biz.isSpecialBusiness) {
            setSpecialBiz(biz);
          }
        });
      })
      .catch((err) => {
        setIsBizLoading(false);
        setSpecialBiz(null);
      });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: TabType) => {
    setSelectedTab(newValue);
  };

  const validateAccountInfo = (infoId: AccountInfoIdType, value: string) => {
    switch (infoId) {
      case "name":
        return "";
      case "taxAgentNumber":
        return "";
      case "mobileNo":
        return "";
      case "email":
        return "";
    }
  };

  const validateNewzenInfo = (infoId: NewzenInfoIdType, value: string) => {
    switch (infoId) {
      case "newzenId":
        return "";
      case "newzenPw":
        return "";
      case "newzenServerIp": {
        if (!value || REGEXP_IPV4_ADDR.test(value)) return "";
        else return "올바른 IP 주소를 입력해주세요.";
      }
      case "newzenServerDrive": {
        if (!value || REGEXP_DRIVE_LETTER.test(value)) return "";
        else return "올바른 드라이브명을 입력해주세요. (영문 대문자 입력)";
      }
    }
  };

  const handleInput = (
    e: React.FormEvent<HTMLInputElement>,
    infoId: UserInfoIdType,
    infoType: infoType
  ) => {
    const { value } = e.currentTarget;

    let tmpId;
    let tmpInfo: any = {};
    let tmpState: any = {};
    let errMsg = "";

    if (infoType === "account") {
      tmpId = infoId as AccountInfoIdType;
      tmpInfo = { ...accountInfo };
      tmpState = { ...accountState };
      errMsg = validateAccountInfo(tmpId, value);
    } else if (infoType === "newzen") {
      tmpId = infoId as NewzenInfoIdType;
      tmpInfo = { ...newzenInfo };
      tmpState = { ...newzenState };
      errMsg = validateNewzenInfo(tmpId, value);
    } else {
      setAlertDialogData({
        message: "입력 타입이 올바르지 않습니다.",
        type: "error",
      });
      setIsAlertOpen(true);
      return;
    }

    tmpInfo[infoId] = value;
    tmpState[infoId] = {
      ...tmpState[infoId],
      errMsg,
      isValid: errMsg === "",
      isEdited: value && !(value === userInfo?.[infoId]),
    };

    if (infoType === "account") {
      setAccountInfo(tmpInfo);
      setAccountState(tmpState);
    } else if (infoType === "newzen") {
      setNewzenInfo(tmpInfo);
      setNewzenState(tmpState);
    }
  };

  const handleEdit = (infoId: UserInfoIdType, infoType: infoType) => {
    let tmpId;

    if (infoType === "account") {
      tmpId = infoId as AccountInfoIdType;
      setAccountState({
        ...accountState,
        [infoId]: {
          ...accountState[tmpId],
          isEdited: true,
        },
      });
    } else if (infoType === "newzen") {
      tmpId = infoId as NewzenInfoIdType;
      setNewzenState({
        ...newzenState,
        [infoId]: {
          ...newzenState[tmpId],
          isEdited: true,
        },
      });
    }
  };

  const handleUndo = (infoId: UserInfoIdType, infoType: infoType) => {
    let tmpId;
    let tmpInfo: any = {};
    let tmpState: any = {};

    if (infoType === "account") {
      tmpId = infoId as AccountInfoIdType;
      tmpInfo = { ...accountInfo };
      tmpState = { ...accountState };
    } else if (infoType === "newzen") {
      tmpId = infoId as NewzenInfoIdType;
      tmpInfo = { ...newzenInfo };
      tmpState = { ...newzenState };
    }

    tmpInfo[infoId] = userInfo?.[infoId] || "";
    tmpState[infoId] = {
      ...tmpState[infoId],
      isEdited: false,
    };

    if (infoType === "account") {
      setAccountInfo(tmpInfo);
      setAccountState(tmpState);
    } else if (infoType === "newzen") {
      setNewzenInfo(tmpInfo);
      setNewzenState(tmpState);
    }
  };

  const handleVisibility = (infoId: NewzenInfoIdType) => {
    {
      setNewzenState({
        ...newzenState,
        [infoId]: {
          ...newzenState[infoId],
          isHidden: !newzenState[infoId].isHidden,
        },
      });
    }
  };

  const checkSaveDisabled = (tab: TabType) => {
    let isEdited = false;
    let isEditedValid = true;

    switch (tab) {
      case "account":
        Object.keys(accountState).forEach((infoId) => {
          const iId = infoId as AccountInfoIdType;
          const infoState = accountState[iId];
          if (infoState.isEdited) {
            if (userInfo?.[iId] === accountInfo[iId]) isEdited = false;
            else isEdited = true;
            if (!infoState.isValid) {
              isEditedValid = false;
            }
          }
        });
        return !(isEdited && isEditedValid);
      case "taxOffice":
        return false;
      case "newzen": {
        Object.keys(newzenState).forEach((infoId) => {
          const iId = infoId as NewzenInfoIdType;
          const infoState = newzenState[iId];
          if (infoState.isEdited) {
            if (userInfo?.[iId] === newzenInfo[iId]) isEdited = false;
            else isEdited = true;
            if (!infoState.isValid) {
              isEditedValid = false;
            }
          }
        });
        return !(isEdited && isEditedValid);
      }
    }
  };

  const verifyMobile = () => {
    const tmpVeriData = { ...verificationData };
    const tmpVeriState = { ...verificationState };
    tmpVeriState.verificationId.isLoading = true;
    tmpVeriState.verificationId.errMsg = "";
    setVerificationState(tmpVeriState);

    sendAuthCodeOnDevice("mobile", accountInfo.mobileNo)
      .then((res) => {
        tmpVeriState.verificationId.isLoading = false;
        tmpVeriState.verificationId.isValid = true;
        tmpVeriState.verificationId.isVerified = true;
        tmpVeriState.verificationId.isDisabled = true;
        setVerificationState(tmpVeriState);

        const veriDataId = res?.data.id;
        tmpVeriData.verificationId = veriDataId;
        setVerificationData(tmpVeriData);
      })
      .catch((err) => {
        const errMsg =
          err.response?.data?.message || "인증번호 발송에 실패했습니다.";

        tmpVeriState.verificationId.isLoading = false;
        tmpVeriState.verificationId.errMsg = errMsg;
        setAlertDialogData({ message: errMsg, type: "error" });
        setIsAlertOpen(true);

        setVerificationState(tmpVeriState);
      });
  };

  const checkMobileVeriCode = () => {
    const tmpVeriState = { ...verificationState };
    tmpVeriState.verificationCode.isLoading = true;
    tmpVeriState.verificationCode.errMsg = "";
    setVerificationState(tmpVeriState);

    checkDeviceVeriCode(
      "mobile",
      verificationData.verificationId,
      verificationData.verificationCode
    )
      .then((res) => {
        tmpVeriState.verificationCode.isLoading = false;
        tmpVeriState.verificationCode.isValid = true;
        tmpVeriState.verificationCode.isVerified = true;
        tmpVeriState.verificationCode.isDisabled = true;
        setVerificationState(tmpVeriState);
      })
      .catch((err) => {
        const errMsg =
          err.response?.data?.message || "인증번호 확인에 실패했습니다.";

        tmpVeriState.verificationCode.isLoading = false;
        tmpVeriState.verificationCode.errMsg = errMsg;
        setAlertDialogData({ message: errMsg, type: "error" });
        setIsAlertOpen(true);

        setVerificationState(tmpVeriState);
      });
  };

  const saveEditedInfo = (tab: TabType) => {
    // 현재는 newzen 정보만 저장하는 로직만 작성

    if (!userPassword) {
      setAlertDialogData({
        message: "계정 비밀번호를 입력해주세요.",
        type: "error",
      });
      setIsAlertOpen(true);
      return;
    }
    const { mobileNo } = userInfo as UserInfo;
    if (!mobileNo) {
      if (tab !== "account" || !accountInfo.mobileNo) {
        setAlertDialogData({
          message: "모바일 번호를 입력해주세요.",
          type: "error",
        });
        setIsAlertOpen(true);
        return;
      }
    }
    const updUserData: UpdateUserData = {
      password: userPassword,
      mobileNo: mobileNo,
    };

    if (tab === "account") {
      Object.keys(accountState).forEach((infoId) => {
        const iId = infoId as AccountInfoIdType;
        if (accountState[iId].isEdited) {
          updUserData[iId] = accountInfo[iId];

          if (iId === "mobileNo") {
            if (
              verificationState.verificationCode.isVerified &&
              verificationState.verificationId.isVerified
            ) {
              updUserData.verification = {
                verificationCode: verificationData.verificationCode,
                verificationId: verificationData.verificationId,
              };
            } else {
              setAlertDialogData({
                message: "모바일 인증을 완료해주세요.",
                type: "error",
              });
              setIsAlertOpen(true);
              return;
            }
          }
        }
      });
    } else if (tab === "newzen") {
      Object.keys(newzenState).forEach((infoId) => {
        const iId = infoId as NewzenInfoIdType;
        if (newzenState[iId].isEdited) {
          updUserData[iId] = newzenInfo[iId];
        }
      });
      updUserData.newzenServerPort = 8500;
    }

    if (userInfo) {
      updateUserInfo(userInfo.id, updUserData)
        .then((res) => {
          closeDialog();
          setIsUserRefresh(true);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() =>
        console.log("외부 클릭 닫기 비활성화. 닫기 버튼으로만 종료")
      }
    >
      <DialogTitle
        css={css`
          background-color: ${PALETTE.background};
        `}
      >
        유저 정보
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedTab}
          onChange={handleChange}
          aria-label="basic tabs example"
          css={css`
            .Mui-selected {
              color: ${PALETTE.primary} !important;
            }
            border-bottom: 1px solid ${PALETTE.border};
          `}
          TabIndicatorProps={{
            style: {
              backgroundColor: PALETTE.primary,
            },
          }}
        >
          <Tab label={`${tabLabel.account} 정보`} value="account" />
          <Tab
            label={`${tabLabel.taxOffice} 정보`}
            value="taxOffice"
            disabled={!isBizLoading && !Boolean(specialBiz)}
          />
          <Tab label={`${tabLabel.newzen} 정보`} value="newzen" />
        </Tabs>
      </DialogContent>
      <TabPanel value={selectedTab} tabId="account" css={panelCss}>
        {userInfo && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 24px;
            `}
          >
            <FormInput
              infoId="email"
              infoType="account"
              userInfo={userInfo}
              selectedInfo={accountInfo}
              selectedState={accountState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
              inputProps={{
                disabled: true,
              }}
            />
            <FormInput
              infoId="name"
              infoType="account"
              userInfo={userInfo}
              selectedInfo={accountInfo}
              selectedState={accountState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
            />
            <FormInput
              infoId="taxAgentNumber"
              infoType="account"
              userInfo={userInfo}
              selectedInfo={accountInfo}
              selectedState={accountState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
            />
            <FormInput
              infoId="mobileNo"
              infoType="account"
              userInfo={userInfo}
              selectedInfo={accountInfo}
              selectedState={accountState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
            />
          </div>
        )}
      </TabPanel>
      <TabPanel value={selectedTab} tabId="taxOffice" css={panelCss}>
        {specialBiz ? (
          <div>
            <p>세무사무소명: {specialBiz.name}</p>
            <p>대표자명: {specialBiz.ceoName}</p>
            <p>사업자등록번호: {specialBiz.regNumber}</p>
            <p>세금계산서 이메일: {specialBiz.taxEmail || "미등록"}</p>
          </div>
        ) : null}
      </TabPanel>
      <TabPanel value={selectedTab} tabId="newzen" css={panelCss}>
        {userInfo && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 24px;
            `}
          >
            <FormInput
              infoId="newzenId"
              infoType="newzen"
              userInfo={userInfo}
              selectedInfo={newzenInfo}
              selectedState={newzenState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
            />
            <FormInput
              infoId="newzenPw"
              infoType="newzen"
              userInfo={userInfo}
              selectedInfo={newzenInfo}
              selectedState={newzenState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
              handleVisibility={handleVisibility}
              inputProps={{
                type: newzenState.newzenPw.isHidden ? "password" : "text",
                autoComplete: "new-password",
                autoSave: "off",
              }}
            />
            <FormInput
              infoId="newzenServerIp"
              infoType="newzen"
              userInfo={userInfo}
              selectedInfo={newzenInfo}
              selectedState={newzenState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
            />
            <FormInput
              infoId="newzenServerDrive"
              infoType="newzen"
              userInfo={userInfo}
              selectedInfo={newzenInfo}
              selectedState={newzenState}
              handleInput={handleInput}
              handleEdit={handleEdit}
              handleUndo={handleUndo}
              inputProps={{
                maxLength: 1,
              }}
            />
          </div>
        )}
      </TabPanel>
      <DialogActions
        css={css`
          padding: 0 24px 16px;
        `}
      >
        <div
          css={css`
            width: 100%;
            padding-top: 16px;
            border-top: 1px solid ${PALETTE.border};
            display: flex;
            justify-content: flex-end;
            gap: 12px;
          `}
        >
          <BaseButton variant="outlined" onClick={closeDialog}>
            닫기
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            disabled={checkSaveDisabled(selectedTab)}
            onClick={openConfirmDialog}
            css={css`
              display: ${selectedTab === "taxOffice" ? "none" : "block"};
            `}
          >
            {tabLabel[selectedTab]} 정보 저장
          </BaseButton>
        </div>
      </DialogActions>
      <Dialog open={isConfirmDialogOpen} onClose={closeConfirmDialog}>
        <DialogTitle
          css={css`
            background-color: ${PALETTE.background};
          `}
        >
          정보 변경
        </DialogTitle>
        <DialogContent
          css={css`
            min-width: 240px;
          `}
        >
          <p
            css={css`
              font-weight: 500;
            `}
          >
            변경 정보 확인
          </p>
          {selectedTab === "account" &&
            Object.keys(accountInfo).map((infoId) => {
              if (accountState[infoId as AccountInfoIdType].isEdited) {
                return (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 12px;
                    `}
                  >
                    <p
                      key={infoId}
                      css={css`
                        font-size: 14px;
                      `}
                    >
                      {accountLabel[infoId as AccountInfoIdType]}:{" "}
                      {accountInfo[infoId as AccountInfoIdType]}
                    </p>
                    {infoId === "mobileNo" && (
                      <BaseButton
                        variant="contained"
                        color={PALETTE.primary}
                        disabled={
                          verificationState.verificationId.isLoading ||
                          verificationState.verificationId.isDisabled
                        }
                        onClick={verifyMobile}
                        css={css`
                          padding: 4px 8px;
                          font-size: 12px;
                        `}
                      >
                        {verificationState.verificationId.isLoading
                          ? "요청중"
                          : verificationState.verificationId.isVerified
                          ? "요청완료"
                          : "인증요청"}
                      </BaseButton>
                    )}
                  </div>
                );
              }
            })}
          {selectedTab === "newzen" &&
            Object.keys(newzenInfo).map((infoId) => {
              if (newzenState[infoId as NewzenInfoIdType].isEdited) {
                return (
                  <p
                    key={infoId}
                    css={css`
                      font-size: 14px;
                    `}
                  >
                    {newzenLabel[infoId as NewzenInfoIdType]}:{" "}
                    {newzenInfo[infoId as NewzenInfoIdType]}
                  </p>
                );
              }
            })}
          {selectedTab === "account" && accountState.mobileNo.isEdited && (
            <div
              css={css`
                margin-bottom: 16px;
                position: relative;
                display: flex;
                align-items: center;
                gap: 16px;
              `}
            >
              <BaseInput
                placeholder="인증번호"
                value={verificationData.verificationCode}
                disabled={!Boolean(verificationData.verificationId)}
                onInput={(e) =>
                  setVerificationData({
                    ...verificationData,
                    verificationCode: e.currentTarget.value,
                  })
                }
                css={css`
                  width: 120px;
                `}
              />
              <BaseButton
                variant="contained"
                color={PALETTE.primary}
                disabled={
                  !verificationData.verificationCode ||
                  verificationState.verificationCode.isLoading ||
                  verificationState.verificationCode.isDisabled
                }
                onClick={checkMobileVeriCode}
                css={css`
                  padding: 4px 8px;
                  font-size: 12px;
                `}
              >
                {verificationState.verificationCode.isLoading
                  ? "확인중"
                  : verificationState.verificationCode.isVerified
                  ? "확인완료"
                  : "인증확인"}
              </BaseButton>
            </div>
          )}
          <div
            css={css`
              position: relative;
            `}
          >
            <BaseInput
              placeholder="계정 비밀번호"
              type="password"
              value={userPassword}
              onInput={(e) => setUserPassword(e.currentTarget.value)}
            />
            <p
              css={css`
                position: absolute;
                bottom: -32px;
                color: ${PALETTE.primary};
                font-size: 12px;
              `}
            >
              {userPassword
                ? PASSWORD_REG_EX.test(userPassword)
                  ? ""
                  : "영문 대소문자, 숫자, 특수문자 포함 8~20자"
                : "계정 비밀번호를 입력해주세요."}
            </p>
          </div>
        </DialogContent>
        <DialogActions
          css={css`
            padding: 16px;
          `}
        >
          <BaseButton
            variant="outlined"
            onClick={closeConfirmDialog}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            disabled={
              !(userPassword && PASSWORD_REG_EX.test(userPassword)) ||
              (accountState.mobileNo.isEdited &&
                !(
                  verificationData.verificationCode ||
                  verificationData.verificationId
                ))
            }
            onClick={() => {
              setIsConfirmDialogOpen(false);
              // 세무사랑 정보 저장 로직 작성
              saveEditedInfo(selectedTab);
            }}
            css={css`
              padding: 8px 16px;
            `}
          >
            저장
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </Dialog>
  );
}

type infoType = "account" | "newzen";

interface FormInputProps {
  infoId: UserInfoIdType;
  infoType: infoType;
  userInfo: UserInfo;
  selectedInfo: AccountInfoData | NewzenData;
  selectedState: AccountInfoState | NewZenState;
  handleInput: (
    e: React.FormEvent<HTMLInputElement>,
    infoId: UserInfoIdType,
    infoType: infoType
  ) => void;
  handleEdit: (infoId: UserInfoIdType, infoType: infoType) => void;
  handleUndo: (infoId: UserInfoIdType, infoType: infoType) => void;
  handleVisibility?: (infoId: NewzenInfoIdType) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

const FormInput = ({
  infoId,
  infoType,
  userInfo,
  selectedInfo,
  selectedState,
  handleInput,
  handleEdit,
  handleUndo,
  handleVisibility,
  inputProps,
}: FormInputProps) => {
  if (!userInfo) return null;

  const tmpInfo: any = selectedInfo;
  const tmpState: any = selectedState;
  const infoData: any = tmpInfo[infoId];
  const stateData = tmpState[infoId];

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <div
        css={css`
          display: flex;
          gap: 12px;
        `}
      >
        <BaseInput
          placeholder={
            accountLabel[infoId as AccountInfoIdType] ||
            newzenLabel[infoId as NewzenInfoIdType]
          }
          disabled={Boolean(userInfo[infoId]) && !Boolean(stateData.isEdited)}
          value={infoData || userInfo[infoId] || ""}
          onInput={(e) => handleInput(e, infoId, infoType)}
          {...inputProps}
        />
        {userInfo[infoId] && !stateData.isDisabled ? (
          stateData.isEdited ? (
            <IconButton onClick={() => handleUndo(infoId, infoType)}>
              <Undo
                css={css`
                  font-size: 16px;
                  color: ${PALETTE.text};
                `}
              />
            </IconButton>
          ) : (
            <IconButton onClick={() => handleEdit(infoId, infoType)}>
              <Edit
                css={css`
                  font-size: 16px;
                  color: ${PALETTE.text};
                `}
              />
            </IconButton>
          )
        ) : null}
        {handleVisibility && (
          <IconButton
            onClick={() => handleVisibility(infoId as NewzenInfoIdType)}
          >
            {stateData.isHidden ? (
              <Visibility
                css={css`
                  font-size: 16px;
                  color: ${PALETTE.text};
                `}
              />
            ) : (
              <VisibilityOff
                css={css`
                  font-size: 16px;
                  color: ${PALETTE.text};
                `}
              />
            )}
          </IconButton>
        )}
      </div>
      <div
        css={css`
          position: absolute;
          bottom: -16px;
          color: ${PALETTE.primary};
          font-size: 12px;
        `}
      >
        {stateData?.errMsg || ""}
      </div>
    </div>
  );
};

interface TabPanelProps {
  tabId: TabType;
  children?: React.ReactNode;
  dir?: string;
  value: TabType;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, tabId, ...other } = props;

  return (
    <div
      id={`userinfo-tabpanel-${tabId}`}
      aria-labelledby={`userinfo-tab-${tabId}`}
      role="tabpanel"
      hidden={value !== tabId}
      {...other}
    >
      {value === tabId && (
        <div
          css={css`
            padding: 0 24px 24px;
          `}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default UserDialog;
